import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { JSX } from 'react';
import { type ControlProps, ControlType } from '../../../../Form';
import { formatPrice } from '../../../../../utils/formatting';
import type { Localization } from '../../../../../common/Localization';
import FilterContainer from '../../FilterContainer';
import FormatDecorated from './FormatDecorated';
import getFieldDynamic from './getFieldDynamic';
import DeferredPaymentsMessage from '../../../../DeferredPayments/DeferredPaymentsMessage';
import type { CommonProps } from '../CommonProps';
import { getMaxPrice } from './priceUtils.ts';

export const priceValue = (searchParams: SearchParams, maxPriceSlider: number) => {
  const min = (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[0], 10)) || 0;
  const max =
    (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[1], 10)) ||
    maxPriceSlider;
  return { min, max };
};

type GetField = {
  t: TranslationFunction;
  localization: Localization;
  fieldName?: string;
  searchMode: SearchModes;
  isWaiting: boolean;
  price: {
    min: number;
    max: number;
  };
};

export const getField = ({ t, localization, fieldName, searchMode, isWaiting, price }: GetField) => {
  const { deferredPaymentLink, lang, currency } = localization;
  const maxPriceSlider = getMaxPrice(lang, currency);
  const formatPriceRange = ({
    minValue,
    maxValue,
    maxAllowed,
  }: {
    minValue: number;
    maxValue: number;
    maxAllowed: number;
  }): string | JSX.Element => {
    const min = formatPrice({ price: minValue, localization });
    const max =
      maxValue === maxAllowed
        ? t('vacation.oss.components.searchfilters.labels.nolimit')
        : formatPrice({ price: maxValue, localization });

    return (
      <FormatDecorated
        min={min}
        max={max}
        minPrice={t('vacation.oss.filters.budget.min_price')}
        maxPrice={t('vacation.oss.filters.budget.max_price')}
        maxPriceSlider={maxPriceSlider.toString()}
      />
    );
  };

  return [
    ...(localization.configValuesApp?.isDeferredPaymentsEnabled && deferredPaymentLink
      ? [
          {
            control: ControlType.LABEL,
            name: 'deferredPaymentsMessage',
            content: <DeferredPaymentsMessage {...{ localization, link: deferredPaymentLink }} />,
          },
        ]
      : []),
    {
      control: ControlType.SLIDER,
      name: fieldName || 'price',
      value: {
        min: price.min,
        max: price.max,
      },
      default: {
        min: 0,
        max: maxPriceSlider,
      },
      minAllowed: 0,
      maxAllowed: maxPriceSlider,
      bulletSize: 20,
      valueLabel: formatPriceRange,
      roundTo: 50,
      className: 'slider-price',
      label:
        searchMode === SearchModes.DP
          ? t('vacation.oss.components.searchfilters.labels.price_person')
          : t('vacation.oss.components.searchfilters.labels.total_price'),
      valuesOnBottom: true,
      ...(isWaiting ? { disabled: true } : {}),
    },
  ];
};

type GetPriceField = {
  searchParams: SearchParams;
  searchMode: SearchModes;
  localization: Localization;
  fieldName?: string;
  commonProps: CommonProps;
  t: TranslationFunction;
};

const getPriceField = ({
  searchParams,
  searchMode,
  localization,
  t,
  fieldName,
  commonProps,
}: GetPriceField): ControlProps => {
  const { lang, currency } = localization;
  const maxPriceSlider = getMaxPrice(lang, currency);
  let controls = [];
  const { dynamicFiltersResponse, isWaiting } = commonProps;

  if (
    dynamicFiltersResponse &&
    dynamicFiltersResponse?.price?.diagram?.length > 0 &&
    dynamicFiltersResponse?.price?.diagram?.some((num) => num !== 0)
  ) {
    const { from, to, diagram, interval } = dynamicFiltersResponse.price;
    let min =
      (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[0], 10)) || from;
    let max =
      (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[1], 10)) || to;
    if (max > to) {
      max = to;
    }
    if (min < from || min > to) {
      min = from;
    }
    const price = {
      min,
      max,
    };

    controls = getFieldDynamic({
      t,
      localization,
      fieldName,
      searchMode,
      price,
      dynamicPrice: { from, to, diagram, interval },
      commonProps,
    });
  } else {
    const priceVal = priceValue(searchParams, maxPriceSlider);
    const price = {
      min: priceVal.min,
      max: priceVal.max,
    };

    controls = getField({
      t,
      localization,
      fieldName,
      searchMode,
      price,
      isWaiting,
    });
  }

  return {
    control: ControlType.CONTAINER,
    name: FilterContainer.PriceContainer,
    top: { text: t('vacation.oss.filters.budget.title') },
    controls,
  };
};

export default getPriceField;
